import React, { useState, useEffect } from 'react';
import { questionAPI } from '../services/api';
import AdminSidebar from "../components/AdminSidebar";
import QuestionModal from './QuestionModal';
import AdminNavbar from '../components/AdminNavbar'; 
import { useSidebar } from '../components/SidebarContext';
import Filter from './Filter';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import { FaBorderNone } from 'react-icons/fa';

function QuestionManager() {
  const { isSidebarOpen } = useSidebar();
  const sidebarWidth = isSidebarOpen ? '15.6rem' : '4.4rem';

  const [questions, setQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({ section: null, difficulty: null });
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [tempFilters, setTempFilters] = useState({ section: null, difficulty: null });
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { t: Date.now() };
      console.log('Fetching questions: params=', JSON.stringify(params));
      const res = await questionAPI.getAll({ params });
      console.log('API response:', JSON.stringify({
        dataLength: res.data.data?.length,
        total: res.data.total,
        firstQuestion: res.data.data?.[0]?.questionText || 'No questions',
        firstQuestionId: res.data.data?.[0]?._id || 'No ID',
        response: res.data
      }, null, 2));
      if (!res.data.data?.length && res.data.total > 0) {
        console.warn('No questions returned. Total:', res.data.total);
      }
      const fetchedQuestions = res.data.data || [];
      setQuestions(fetchedQuestions);
      applyFilters(fetchedQuestions, selectedFilters, searchQuery);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching questions:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      setError('Failed to load questions. Please try again later.');
      setLoading(false);
    }
  };

  const applyFilters = (questionsToFilter, filters, query = '') => {
    let filtered = [...questionsToFilter];
    if (filters.section) {
      filtered = filtered.filter(q => (q.section || q.part) === filters.section);
    }
    if (filters.difficulty) {
      filtered = filtered.filter(q => (q.difficulty || 'easy').toLowerCase() === filters.difficulty.toLowerCase());
    }
    if (query) {
      filtered = filtered.filter(q =>
        (q.questionText || q.text || '').toLowerCase().includes(query.toLowerCase())
      );
    }
    setFilteredQuestions(filtered);
  };

  const handleFilterChange = (newFilters) => {
    setTempFilters(newFilters);
  };

  const applyFilterModal = () => {
    setSelectedFilters(tempFilters);
    applyFilters(questions, tempFilters, searchQuery);
    setShowFilterModal(false);
  };

  const handleOpenFilterModal = () => {
    setTempFilters(selectedFilters);
    setShowFilterModal(true);
  };

  const handleCloseFilterModal = () => {
    setShowFilterModal(false);
    setTempFilters(selectedFilters);
  };

  const removeFilter = (type) => {
    const newFilters = { ...selectedFilters, [type]: null };
    setSelectedFilters(newFilters);
    applyFilters(questions, newFilters, searchQuery);
  };

  const clearAllFilters = () => {
    const newFilters = { section: null, difficulty: null };
    setSelectedFilters(newFilters);
    applyFilters(questions, newFilters, searchQuery);
  };

  const handleAddQuestion = () => {
    setSelectedQuestion(null);
    setShowModal(true);
    console.log('Opening modal to add a new question');
  };

  const handleEdit = (question) => {
    const questionToEdit = {
      ...question,
      _id: question._id,
      questionText: question.questionText || question.text || '',
      options: question.options || [],
      correctAnswer: question.correctAnswer || '',
      section: question.section || question.part || '',
      difficulty: question.difficulty || 'easy'
    };
    console.log('Editing question:', questionToEdit);
    setSelectedQuestion(questionToEdit);
    setShowModal(true);
  };

  const handleView = (question) => {
    alert(`📄 Question: ${question.questionText || question.text}\n✅ Correct Answer: ${question.correctAnswer}\n📚 Section: ${question.section || question.part}\n🎯 Difficulty: ${question.difficulty || 'Not specified'}\n🔘 Options:\n${(question.options || []).map((opt, i) => `${i + 1}. ${opt}`).join('\n')}`);
  };

  const handleSaveQuestion = async (data) => {
    try {
      console.log('Saving question data:', data);
      const questionData = {
        questionText: data.questionText,
        options: data.options,
        correctAnswer: data.correctAnswer,
        section: data.section,
        difficulty: data.difficulty
      };

      if (selectedQuestion) {
        await questionAPI.update(selectedQuestion._id, questionData);
        console.log('Question updated successfully');
      } else {
        await questionAPI.create(questionData);
        console.log('Question created successfully');
        setShowSuccessDialog(true);
      }
      setShowModal(false);
      setSelectedQuestion(null);
      fetchQuestions();
    } catch (error) {
      console.error('Error saving question:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      alert(`Failed to save question: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this question?')) {
      try {
        await questionAPI.delete(id);
        console.log('Question deleted successfully');
        fetchQuestions();
      } catch (error) {
        console.error('Error deleting question:', error);
        alert('Failed to delete the question. Please try again.');
      }
    }
  };

  const handleCloseSuccessDialog = () => {
    setShowSuccessDialog(false);
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    applyFilters(questions, selectedFilters, query);
  };

  const sections = [...new Set(questions.map(q => q.section || q.part).filter(Boolean))];
  const difficulties = [...new Set(questions.map(q => (q.difficulty || 'easy').toLowerCase()))];

  const styles = {
    container: {
      display: 'flex',
      height: '100vh',
      overflow: 'hidden',
      minHeight: "100vh",
      background: "linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF)",
    },
    sidebar: {
      width: sidebarWidth,
      borderRadius: '30px',
      height: '100vh',
      flexShrink: 0, // Prevent sidebar from shrinking
    },
    content: {
      flex: 1,
      padding: '15px',
      margin: '6rem 2rem 2rem 2rem', // Equal left and right margins (2rem), 6rem top, 2rem bottom
      backgroundColor: 'white',
      overflowY: 'auto',
      transition: 'margin-left 0.3s ease',
      minHeight: 'calc(100vh - 8rem)',
      borderTopLeftRadius: '1rem',
      borderTopRightRadius: '1rem',
      borderBottomLeftRadius: '1rem',
      borderBottomRightRadius: '1rem',
      maxWidth: `calc(100vw - ${sidebarWidth} - 4rem)`, // Ensure content fits: viewport - sidebar - (left + right margins)
      width: '100%', // Take up available width up to maxWidth
    },
    headerContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between', // Spreads items across the row
      alignItems: 'center',
      marginBottom: '16px',
      width: '100%',
    },
    title: { fontSize: '24px', fontWeight: '500', color: '#333' },
    buttonContainer: {
      display: 'flex',
      alignItems: 'center', // Align button vertically with search/filter
    },
    addButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '6px 16px',
      fontSize: '14px',
      fontWeight: '600',
      color: '#6B21A8',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      cursor: 'pointer',
      position: 'relative',
      borderRadius: '30px',
      border: '2px solid transparent',
      backgroundImage: 'linear-gradient(#ffff, #ffff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      transition: 'all 0.4s ease',
    },
    searchFilterGroup: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '8px', // Space between search input and filter icon
    },
    searchInput: {
      width: '100%',
      maxWidth: '300px',
      padding: '6px 12px',
      borderRadius: '30px',
      border: '1px solid #ddd',
      fontSize: '14px',
      outline: 'none',
      transition: 'border-color 0.3s ease',
    },
    searchInputFocus: {
      borderColor: '#1890ff',
    },
    filterIcon: {
      color: '#6B21A8',
      cursor: 'pointer',
      fontSize: '24px',
    },
    filterTagsContainer: { 
      display: 'flex', 
      alignItems: 'center', 
      gap: '8px', 
      marginBottom: '16px', 
      flexWrap: 'wrap',
      justifyContent: 'flex-end',
    },
    selectedFilterTag: { display: 'inline-flex', alignItems: 'center', background: 'linear-gradient(to right, #FFE3E3 , #D6E6FF, #EBD6FF)', color: '#1F2937', padding: '4px 8px', borderRadius: '12px', fontSize: '12px', marginBottom: '4px' },
    removeFilterButton: { marginLeft: '6px', background: 'none', border: 'none', color: '#1F2937', cursor: 'pointer', fontSize: '12px' },
    clearAllButton: { background: '#fff1f0', color: '#f5222d', border: '1px solid #f5222d', borderRadius: '4px', padding: '4px 8px', fontSize: '12px', cursor: 'pointer' },
    filterModalOverlay: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    filterModal: { backgroundColor: 'white', padding: '16px', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)', width: '100%', maxWidth: '400px' },
    filterModalHeader: { fontSize: '16px', fontWeight: '500', marginBottom: '16px', color: '#333' },
    filterModalButtons: { display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' },
    modalButton: { backgroundColor: '#1890ff', color: 'white', border: 'none', borderRadius: '4px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer', transition: 'background-color 0.3s ease' },
    modalButtonCancel: {
      backgroundImage: 'linear-gradient(#fff, #fff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      border: '2px solid transparent',
      borderRadius: '30px',
      padding: '6px 16px',
      fontSize: '14px',
      fontWeight: 'bold',
      color: 'purple',
      cursor: 'pointer',
      transition: 'all 0.4s ease',
    },
    modalButtonHover: {
      backgroundImage: 'linear-gradient(#fff, #fff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      color: '#8A2BE2', // Optional: slightly different hover text color
      transform: 'scale(1.03)',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '12px',
      border: 'none',
    },
    thead: {
      background: 'linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF)',
    },
    th: {
      padding: '8px 10px',
      textAlign: 'center',
      fontWeight: '500',
      color: '#6B21A8',
    }, 
    tbody: {
      borderBottom: '1px solid #B0BEC5',
    },
    td: {
      padding: '10px 12px',
      color: '#333',
      textAlign: 'center',
    },
    actionButtons: { display: 'flex', justifyContent: 'center', gap: '4px' },
    viewButton: { backgroundColor: 'transparent', color: '#cc8888', border: 'none', borderRadius: '2px', padding: '2px', cursor: 'pointer' },
    editButton: { backgroundColor: 'transparent', color: '#5f77cc', border: 'none', borderRadius: '1px', padding: '1px', cursor: 'pointer' },
    deleteButton: { backgroundColor: 'transparent', color: '#7a5fcc', border: 'none', borderRadius: '1px', padding: '1px', cursor: 'pointer' },
    dialogOverlay: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    dialogBox: { backgroundColor: 'white', padding: '16px', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)', textAlign: 'center', maxWidth: '400px', width: '100%' },
    dialogMessage: { fontSize: '14px', color: '#333', marginBottom: '16px' },
    dialogButton: { backgroundColor: '#1890ff', color: 'white', border: 'none', borderRadius: '4px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer', transition: 'background-color 0.3s ease' },
    dialogButtonHover: { backgroundColor: '#40a9ff' }
  };

  return (
    <>
      <style>
        {`
          @keyframes glow {
            0% { box-shadow: 0 0 12px rgba(130, 147, 183, 0.9), 0 0 18px rgba(187, 125, 245, 0.7); }
            50% { box-shadow: 0 0 18px rgba(100, 150, 255, 1), 0 0 25px rgba(180, 100, 255, 0.9); }
            100% { box-shadow: 0 0 12px rgba(104, 137, 207, 0.9), 0 0 18px rgba(180, 100, 255, 0.7); }
          }
          .add-question-btn:hover {
            transform: scale(1.05);
            animation: glow 1.5s ease-in-out infinite;
          }
          .filter-icon:hover {
            color: #40a9ff;
            transform: scale(1.1);
          }
          .search-input:focus {
            border-color: ${styles.searchInputFocus.borderColor};
          }
          /* Alternating row colors for the table */
          tbody tr:nth-child(odd) {
            background-color: #FFFFFF;
          }
          tbody tr:nth-child(even) {
            background: linear-gradient(to right, #FAFCFF, #FBF6FF, #FFF8FB);
          }
        `}
      </style>
      <div style={styles.container}>
        <AdminNavbar />
        <div style={styles.sidebar}>
          <AdminSidebar />
        </div>
        <div style={styles.content}>
          {/* Header with Add Question Button, Search Bar, and Filter Icon */}
          <div style={styles.headerContainer}>
            <div style={styles.buttonContainer}>
              <button
                onClick={handleAddQuestion}
                style={styles.addButton}
                className="add-question-btn"
              >
                Add Question
              </button>
            </div>
            <div style={styles.searchFilterGroup}>
              <input
                type="text"
                placeholder="Search questions..."
                value={searchQuery}
                onChange={handleSearchChange}
                style={styles.searchInput}
                className="search-input"
                aria-label="Search questions"
              />
              <FilterListIcon
                style={styles.filterIcon}
                className="filter-icon"
                onClick={handleOpenFilterModal}
                title="Open filter"
                aria-label="Open filter modal"
              />
            </div>
          </div>

          {/* Filter Tags */}
          {(selectedFilters.section || selectedFilters.difficulty) && (
            <div style={styles.filterTagsContainer}>
              {selectedFilters.section && (
                <span style={styles.selectedFilterTag}>
                  Section: {selectedFilters.section.charAt(0).toUpperCase() + selectedFilters.section.slice(1)}
                  <button
                    onClick={() => removeFilter('section')}
                    style={styles.removeFilterButton}
                    aria-label="Remove section filter"
                  >
                    ✕
                  </button>
                </span>
              )}
              {selectedFilters.difficulty && (
                <span style={styles.selectedFilterTag}>
                  Difficulty: {selectedFilters.difficulty.charAt(0).toUpperCase() + selectedFilters.difficulty.slice(1)}
                  <button
                    onClick={() => removeFilter('difficulty')}
                    style={styles.removeFilterButton}
                    aria-label="Remove difficulty filter"
                  >
                    ✕
                  </button>
                </span>
              )}
              <button
                onClick={clearAllFilters}
                style={styles.clearAllButton}
              >
                Clear All
              </button>
            </div>
          )}

          {/* Filter Modal */}
          {showFilterModal && (
            <div style={styles.filterModalOverlay}>
              <div style={styles.filterModal}>
                <h2 style={styles.filterModalHeader}>Filter Questions</h2>
                <Filter 
                  sections={sections}
                  difficulties={difficulties}
                  selectedFilters={tempFilters}
                  onFilterChange={handleFilterChange}
                  inModal={true}
                />
                <div style={styles.filterModalButtons}>
                  <button
                    onClick={handleCloseFilterModal}
                    style={styles.modalButtonCancel}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={applyFilterModal}
                    style={styles.modalButton}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = styles.modalButtonHover.backgroundColor}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = styles.modalButton.backgroundColor}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Questions Table */}
          <div style={{ width: '100%', overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead style={styles.thead}>
                <tr>
                  <th style={styles.th}>Sr.</th>
                  <th style={{ ...styles.th, textAlign: 'left' }}>Question</th>
                  <th style={{ ...styles.th, textAlign: 'left' }}>Options</th>
                  <th style={styles.th}>Correct Answer</th>
                  <th style={styles.th}>Section</th>
                  <th style={styles.th}>Difficulty</th>
                  <th style={{ ...styles.th, textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody style={styles.tbody}>
                {loading ? (
                  <tr>
                    <td colSpan="7" style={{ ...styles.td, textAlign: 'center', color: '#666' }}>
                      Loading questions...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan="7" style={{ ...styles.td, textAlign: 'center', color: '#ff6b6b' }}>
                      {error}
                    </td>
                  </tr>
                ) : (
                  filteredQuestions.length > 0 ? (
                    filteredQuestions.map((question, index) => (
                      <tr key={question._id} style={styles.tbody}>
                        <td style={styles.td}>{index + 1}</td>
                        <td style={{ ...styles.td, textAlign: 'left' }}>{question.questionText || question.text}</td>
                        <td style={{ ...styles.td, textAlign: 'left' }}>
                          <ul style={{ margin: 0, paddingLeft: '20px' }}>
                            {(question.options || []).map((opt, i) => (
                              <li key={i}>{opt}</li>
                            ))}
                          </ul>
                        </td>
                        <td style={styles.td}>{question.correctAnswer}</td>
                        <td style={styles.td}>{question.section || question.part}</td>
                        <td style={{ ...styles.td, textTransform: 'capitalize' }}>
                          {question.difficulty || 'Not specified'}
                        </td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          <div style={styles.actionButtons}>
                            <button
                              onClick={() => handleView(question)}
                              style={styles.viewButton}
                              title="View"
                            >
                              <VisibilityIcon style={{ fontSize: '17px' }} />
                            </button>
                            <button
                              onClick={() => handleEdit(question)}
                              style={styles.editButton}
                              title="Edit"
                            >
                              <EditIcon style={{ fontSize: '17px' }} />
                            </button>
                            <button
                              onClick={() => handleDelete(question._id)}
                              style={styles.deleteButton}
                              title="Delete"
                            >
                              <DeleteIcon style={{ fontSize: '17px' }} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                        No questions found
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>

          {/* Modals */}
          <QuestionModal
            show={showModal}
            handleClose={() => {
              setShowModal(false);
              setSelectedQuestion(null);
            }}
            onSave={handleSaveQuestion}
            selectedQuestion={selectedQuestion}
          />
          {showSuccessDialog && (
            <div style={styles.dialogOverlay}>
              <div style={styles.dialogBox}>
                <p style={styles.dialogMessage}>Successfully Added Question!</p>
                <button
                  onClick={handleCloseSuccessDialog}
                  style={styles.dialogButton}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = styles.dialogButtonHover.backgroundColor}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = styles.modalButton.backgroundColor}
                >
                  OK
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default QuestionManager;